import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TellescopeModelFromName, useTellescopeQuery } from "@tellescope/tanstack-query";
import { formatDateTime, formatName } from "@tellescope/utils";
import { BlockProps } from "../types";
import { useBlock } from ".";
import { usePDFActions } from "./media";

export interface UseFormsBlockArgs extends BlockProps {}

export const useFormsBlock = (args: UseFormsBlockArgs) => {
  const { t } = useTranslation("forms");

  const block = useBlock({ ...args, type: "forms.forms", select: (enduser) => enduser });

  const [selectedForm, setSelectedForm] = useState<{ formId: string; formTitle: string }>();
  const [selectedFormResponse, setSelectedFormResponse] = useState<TellescopeModelFromName<"form_responses">>();
  const [showNewSubmission, setShowNewSubmission] = useState(false);

  const openNewSubmission = useCallback(() => setShowNewSubmission(true), []);
  const closeNewSubmission = useCallback(() => setShowNewSubmission(false), []);

  const openForm = useCallback((form: { formId: string; formTitle: string }) => setSelectedForm(form), []);
  const openFormResponse = useCallback(
    (response: TellescopeModelFromName<"form_responses">) => setSelectedFormResponse(response),
    []
  );
  const closeForm = useCallback(() => {
    setSelectedForm(undefined);
    setSelectedFormResponse(undefined);
  }, []);
  const closeFormResponse = useCallback(() => setSelectedFormResponse(undefined), []);

  const enduser = block.enduserQuery.data;
  const enduserName = enduser ? formatName(enduser) : "";

  // Theme is fetched outside of the PDF render so the logo is already a data URL by the time the
  // generator captures the printable content.
  const { data: { theme } = {} } = useTellescopeQuery({
    model: "organizations",
    method: "get_theme",
    request: { businessId: selectedFormResponse?.businessId ?? "" },
    enabled: !!selectedFormResponse?.businessId,
  });

  const pdfActions = usePDFActions({
    filename: `${(selectedForm?.formTitle ?? t("Form")).replace(/\W/g, "-")}-${
      selectedFormResponse?.submittedAt?.toISOString() ?? "latest"
    }`,
    enduserId: enduser?.id ?? "",
    logoUrl: theme?.logoURL,
    uploadSuccessMessage: t("PDF saved to profile"),
    uploadErrorMessage: t("Failed to save PDF"),
  });

  const breadcrumbs = useMemo(() => {
    const crumbs: { title: string; onClick?: () => void }[] = [
      {
        title: t("Forms"),
        onClick: selectedForm || selectedFormResponse ? closeForm : undefined,
      },
    ];

    if (selectedForm) {
      crumbs.push({
        title: selectedForm.formTitle,
        onClick: selectedFormResponse ? closeFormResponse : undefined,
      });
    }

    if (selectedFormResponse) {
      crumbs.push({
        title: selectedFormResponse.submittedAt
          ? formatDateTime(selectedFormResponse.submittedAt, "MM/DD/YYYY at hh:mm:ss a")
          : t("Response"),
      });
    }

    return crumbs;
  }, [selectedForm, selectedFormResponse, closeForm, closeFormResponse, t]);

  return {
    ...block,
    enduser,
    enduserName,
    selectedForm,
    selectedFormResponse,
    openForm,
    openFormResponse,
    closeForm,
    closeFormResponse,
    showNewSubmission,
    openNewSubmission,
    closeNewSubmission,
    breadcrumbs,
    pdfActions,
  };
};
